// MessageBubble.js (updated)

import React, { useMemo, useState, useRef, useEffect } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { Streamdown } from 'streamdown';

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Avatar from "../../../../theme/layout/partials/Avatar";
import Divider from "../../../../theme/partials/Divider";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { isEmpty, sanitizeAssistantOutput, sizeDownHeaders, textToClipboard } from "../../../../helpers/Functions";
import LoadingText from "../../../../theme/partials/LoadingText";
import TextView from "../../../../theme/partials/Typography";
import {  Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import AIMessageFeedbackControls from "../ai-feedback/AIMessageFeedbackControls";

const MessageBubble = ({
	showMeta = false,
	messageThread = [],
	messagesEndRef,
	isSubmitting,
	partialResponse,
	runStatus = "Thinking",
	getFilesDownload,
	aiSettings,
	rerenderMarkdown = () => {},
	withFeedbackControls = true,
	...props
}) => {
	const [expandedStates, setExpandedStates] = useState({});
	const [showExpandButtonStates, setShowExpandButtonStates] = useState({});
	const [contentHeights, setContentHeights] = useState({});
	const messageRefs = useRef([]);

	const isImage = (fileName) => {
		if (!fileName) return false;
		const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
		const fileExtension = fileName.split('.').pop().toLowerCase();
		return imageExtensions.includes(fileExtension);
	};

	const toggleExpand = (index) => {
		setExpandedStates(prev => ({
			...prev,
			[index]: !prev[index]
		}));
	};

	const latestUserIndex = useMemo(() => {
		for (let i = messageThread.length - 1; i >= 0; i--) {
			if (messageThread[i].role === "user") return i;
		}
		return -1;
	}, [messageThread]);

	// Measure content heights and compute if minimized view would overflow for user messages
	useEffect(() => {
		messageRefs.current.forEach((ref, index) => {
			if (!ref) return;
			const message = messageThread[index];
			if (!message) return;

			// Record full content height for expansion without scroll
			setContentHeights(prev => ({
				...prev,
				[index]: ref.scrollHeight || 0,
			}));

			// Only user messages can be minimized/expanded
			const isUserMsg = message.role !== 'assistant';
			const isImgMsg = isImage(message?.file_name);
			let hasOverflowMin = false;
			if (isUserMsg) {
				// Minimized thresholds: text 18vh, images 60vh
				const thresholdPx = Math.floor((isImgMsg ? 0.60 : 0.18) * window.innerHeight);
				hasOverflowMin = (ref.scrollHeight || 0) > thresholdPx;
			}
			setShowExpandButtonStates(prev => ({
				...prev,
				[index]: hasOverflowMin,
			}));
		});
	}, [messageThread]);


	// Track which code block was copied (by message index)
	const [copiedCodeIndex, setCopiedCodeIndex] = useState(null);
	const handleCopyCode = (codeString, idx) => {
		navigator.clipboard.writeText(codeString);
		setCopiedCodeIndex(idx);
		setTimeout(() => setCopiedCodeIndex(null), 1200);
	};

	const getRenderers = (idx, code) => {
		// 1. Base renderers (Tables, etc.)
		const renderers = {
			table: ({ children }) => <TableContainer sx={{ my: 2 }}><Table size="small">{children}</Table></TableContainer>,
			thead: ({ children }) => <TableHead>{children}</TableHead>,
			tbody: ({ children }) => <TableBody>{children}</TableBody>,
			tr: ({ children }) => <TableRow>{children}</TableRow>,
			th: ({ children }) => <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>{children}</TableCell>,
			td: ({ children }) => <TableCell sx={{ borderBottom: '1px solid #eee' }}>{children}</TableCell>
		};

		// 2. Only attach the custom code renderer if the toggle is ON
		if (code) {
			renderers.code = ({ node, inline, className, children, ...props }) => {
				const codeString = String(children).replace(/\n$/, '');

				// FIX: If it is inline code (e.g. `word`), render a simple span/code
				const isInline = inline || !codeString.includes('\n');

				if (isInline) {
					// Render as a simple span (or styled code tag) to keep it in the text flow
					return (
						<code 
							className={className} 
							{...props} 
							style={{ 
								backgroundColor: '#f6f8fa', 
								color: '#24292e',
								padding: '0.2em 0.4em', 
								borderRadius: '6px',
								fontSize: '85%',
								fontFamily: 'monospace'
							}}
						>
							{children}
						</code>
					);
				}
				// If it is a BLOCK (triple backticks), render the Box with Copy Button
				return (
					<Box sx={{ position: 'relative', mb: 1 }}>
						<pre style={{ margin: 0 }}>
							<code className={className} {...props}>
								{children}
							</code>
						</pre>

						<IconButton
							size="small"
							onClick={() => handleCopyCode(codeString, idx)}
							sx={{
								position: 'absolute',
								top: 6,
								right: 6,
								zIndex: 2,
								background: '#fff',
								border: '1px solid #eee',
								p: 0.5,
							}}
							title={copiedCodeIndex === idx ? 'Copied!' : 'Copy code'}
						>
							<ContentCopyIcon
								fontSize="small"
								color={copiedCodeIndex === idx ? 'success' : 'inherit'}
							/>
						</IconButton>
					</Box>
				);
			};
		}

		return renderers;
	};

	return messageThread.map((message, index) => {
		const isLastMessage = index === messageThread.length - 1;
		const isLatestUser = index === latestUserIndex;
		const isExpanded = expandedStates[index];
		const isUserMessage = message.role !== "assistant";
		const mustExpand = showExpandButtonStates[index];
		const isImageMessage = isImage(message?.file_name)

		// Heights for user messages: no scrollbars, hide overflow when minimized
		const minimizedHeight = isImageMessage ? '60vh' : '18vh';
		const containerMaxHeight = isUserMessage
			? (isExpanded ? undefined : minimizedHeight)
			: undefined;
		const containerOverflowY = 'hidden';

		if (process.env.REACT_APP_ENV === 'development') {
			console.log(sanitizeAssistantOutput(message.content));
		}
		
		return (
			<Box key={index}>
				<Box
					ref={(el) => {
						if (isLatestUser && messagesEndRef) {
							messagesEndRef.current = el;
						}
					}}
					className={`d-flex ${isUserMessage ? "justify-content-end" : "justify-content-start"
						} ${isUserMessage ? "align-items-center" : "align-items-start"} mb-3`
					}
				>
					{!isUserMessage && (
						<Box className="d-flex flex-column align-items-start py-3 h-100">
							<Avatar 
								lib_img={process.env.REACT_APP_S3_BUCKET + "/resource_uploads/otoma_logo_green.png"} 
								customclasses="ms-3"
								transparentBg={true}
							/>
						</Box>
					)}
					<Box
						className={`my-2 ${isUserMessage ? "user-chat-container bubble-chat-container" : "ai-chat-container"
							}`}
					>
						<Box
							ref={el => messageRefs.current[index] = el}
							sx={{
								paddingX: {
									xm: 0,
									md: isUserMessage ? "1rem" : "2vw",
								},
								overflowX: 'auto',
								minHeight: '2.5rem',
								wordWrap: 'break-word',
									maxHeight: isUserMessage ? containerMaxHeight : undefined,
									overflowY: isUserMessage ? containerOverflowY : 'visible',
								maxWidth: '80vw',
							}}
						>
							{
								(message?.role === "content" && message.file_name) ? (
									isImageMessage ? (
										<img
											src={process.env.REACT_APP_S3_BUCKET + message.file_location}
											alt={message.file_name}
											style={{ maxWidth: '25vw', minWidth: '500px', height: 'auto', borderRadius: '8px' }}
										/>
									) : (
										<a className="color-primary" href={process.env.REACT_APP_S3_BUCKET + message.file_location} target="_blank" rel="noopener noreferrer">
											{message.file_name}
										</a>
									)
								) : (
									<div className="ai-response">
										<Streamdown>{isEmpty(message.s3_url) ? sanitizeAssistantOutput(message.content) : message.content}</Streamdown>
										{
											(!isEmpty(message.s3_url)) && (
												isImage(message.s3_url) ? (
													<img
														src={process.env.REACT_APP_S3_BUCKET + message.s3_url}
														alt="Generated content"
														style={{ maxWidth: '25vw', minWidth: '500px', height: 'auto', borderRadius: '8px', marginTop: '1rem' }}
													/>
												) : (
													<a 
														className="color-primary" 
														href={process.env.REACT_APP_S3_BUCKET + message.s3_url} 
														target="_blank" 
														rel="noopener noreferrer"
														style={{ display: 'block', marginTop: '1rem' }}
													>
														Download File
													</a>
												)
											)
										}
									</div>
								)
							}
						</Box>
						{showMeta && (
							(() => {
								if (message?.role === "assistant") return null;

								const createdAt = message.createdAt || message.created_at;
								const inputTokens = message.token_prompt ?? message.token_prompt;
								const outputTokens = message.token_completion ?? message.token_completion;
								const totalTokens = message.token_total ?? message.token_total ?? (typeof inputTokens === 'number' && typeof outputTokens === 'number' ? inputTokens + outputTokens : undefined);

								return (
									<Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'end', gap: 1 }}>
										<TextView fontcolor="secondary" fontsize="small">
											{new Date(createdAt).toLocaleTimeString()}
										</TextView>
									</Box>
								);
							})()
						)}
						{isUserMessage && (mustExpand || !isEmpty(message?.file_ids)) && (
							<Box sx={{ borderTop: "1px outset black" }} className="d-flex flex-column align-items-center">
								<Box
									className={
										(mustExpand && !isEmpty(message?.file_ids))
											? "w-100 d-flex justify-content-around"
											: "w-100 d-flex justify-content-center"
									}
								>
									{mustExpand && (
										<IconButton
											onClick={() => toggleExpand(index)}
											title={isExpanded ? "Minimize" : "Expand"}
											className="font-size-large" 
											sx={{ color: 'gray' }}
										>
											{isExpanded ? (
												<>Show Less <ExpandLessIcon sx={{ color: 'gray' }} /></>
											) : (
												<>Show More <ExpandMoreIcon sx={{ color: 'gray' }} /></>
											)}
										</IconButton>
									)}
									{!isEmpty(message?.file_ids) && (
										<TextView
											className={`
											${mustExpand
													? "ms-2"
													: "w-100 text-center"
												}
											mt-2 clickable-text
										`}
											onClick={(e) => {
												getFilesDownload(JSON.stringify(message?.file_ids))
											}}
										>
											Show Files
										</TextView>
									)}
								</Box>
							</Box>
						)}
					</Box>

					{isUserMessage && (
						<Box className="d-flex flex-column align-items-center py-3 h-100">
							{!props.quickAccess && (
								<Avatar userdetail={props.user_details} customclasses="ms-3" />
							)}
						</Box>
					)}
				</Box>

				{!isUserMessage && (
					<>
						<Divider className="px-3" />
						<Box className="d-flex justify-content-end my-2">
							{withFeedbackControls && (
								<AIMessageFeedbackControls 
									messageId={message?.message_id} 
									activeType={message?.user_feedback?.feedback_type}
								/>
							)}
							<IconButton title="Copy">
								<ContentCopyIcon onClick={() => textToClipboard(message.content, "Message Copied.")} />
							</IconButton>
						</Box>
					</>
				)}
				{(isSubmitting && isLastMessage) && (
					<Box className="d-flex justify-content-start my-2 px-5">
						<LoadingText
							title={runStatus}
							color="black"
						/>
					</Box>
				)}
			</Box>
		);
	});
};

export default MessageBubble;